import { getLocale } from './locale'

/** Options shared by every number formatter, so callers only pass what differs. */
type NumberOptions = Intl.NumberFormatOptions
type DateOptions = Intl.DateTimeFormatOptions

/** Anything a date can arrive as — an API timestamp, a `Date`, or nothing. */
type DateInput = string | number | Date | null | undefined

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * A number with the user's own group and decimal separators — "1,234.5" or
 * "1 234,5" depending on the locale they picked.
 */
export function formatNumber(value: number, options?: NumberOptions): string {
  return new Intl.NumberFormat(getLocale(), options).format(value)
}

/** A whole number, rounded — the usual shape of a stat count. */
export function formatCount(value: number): string {
  return formatNumber(Math.round(value), { maximumFractionDigits: 0 })
}

/** A number shortened for tight spaces — "1.2M", "12 k". */
export function formatCompact(value: number, options?: NumberOptions): string {
  return formatNumber(value, { notation: 'compact', ...options })
}

/**
 * A percentage as a plain number followed by a sign, e.g. "12,5 %". The value
 * is already a percentage, not a fraction, which is how the stats API reports
 * rates.
 */
export function formatPercent(value: number, fractionDigits = 2): string {
  return `${formatNumber(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`
}

/** A date, medium-length by default — "14 Sep 2026". Empty for a bad date. */
export function formatDate(
  value: DateInput,
  options: DateOptions = { dateStyle: 'medium' }
): string {
  const date = toDate(value)
  return date ? date.toLocaleDateString(getLocale(), options) : ''
}

/**
 * A calendar day (`yyyy-MM-dd`) the way the user writes dates. The day carries
 * no zone, so it is read as-is rather than shifted into the browser's.
 */
export function formatCalendarDay(day: string, options?: DateOptions): string {
  const parsed = parseCalendarDay(day)
  return parsed ? formatDate(parsed, options) : ''
}

/**
 * A calendar day as a `Date` in the browser's own zone. Read as-is: parsing
 * `yyyy-MM-dd` the standard way makes it UTC midnight, which is the day before
 * for anyone west of Greenwich.
 */
export function parseCalendarDay(day: string): Date | null {
  const [year, month, date] = (day ?? '').split('-').map(Number)
  if (!year || !month || !date) return null
  return new Date(year, month - 1, date)
}

/** A date with the time of day, for audit trails and note stamps. */
export function formatDateTime(
  value: DateInput,
  options: DateOptions = { dateStyle: 'medium', timeStyle: 'short' }
): string {
  const date = toDate(value)
  return date ? date.toLocaleString(getLocale(), options) : ''
}

/** Just the time of day, for the intraday points of a chart. */
export function formatTime(
  value: DateInput,
  options: DateOptions = { hour: '2-digit', minute: '2-digit' }
): string {
  const date = toDate(value)
  return date ? date.toLocaleTimeString(getLocale(), options) : ''
}

/**
 * A file size in the largest unit that keeps it readable — "845 kB", "1.4 MB".
 * Uses the decimal units browsers and the API report in, with the user's own
 * decimal separator.
 */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return formatNumber(0, { maximumFractionDigits: 0 }) + ' B'
  const units = ['B', 'kB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log10(bytes) / 3), units.length - 1)
  const value = bytes / 1000 ** exponent
  return `${formatNumber(value, { maximumFractionDigits: exponent === 0 ? 0 : 1 })} ${units[exponent]}`
}
